/**
 * The two facts about an artifact that are only known AFTER `stageFor` has already returned.
 *
 * `stageFor` is a pure function of the entity, called in the host's render body, and that is the right
 * shape for it — but two of the numbers the pane needs are not in the blob:
 *
 * 1. **An image's real pixel size.** `BirtvyImage.width/height` is the canvas INSERT box (aspect
 *    preserved, longest side clamped to 640 — see `imageInsertSize`), not the bitmap. The bitmap has to
 *    be loaded to be measured, and loading is asynchronous.
 * 2. **Whether a renderer threw.** A render error is discovered strictly after the props that caused it
 *    were computed.
 *
 * Both are held here, in a module store, so `stageFor` can read them synchronously. A plain
 * `stageFor` therefore picks each of them up on the host's NEXT render, which is honest but late;
 * `useStageFor` subscribes to this store and picks them up immediately. Nothing else in the route has
 * to know either exists.
 */
import { measureImage } from '~/design-system/components/DeckGen/birtvy/birtvyImage';
import type { DeckEntity } from '../deckContent';
import { itemKey } from '../ids';

const listeners = new Set<() => void>();
let version = 0;

function bump(): void {
  version += 1;
  for (const listener of listeners) listener();
}

/** `useSyncExternalStore`'s subscribe half. */
export function subscribeStage(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** A counter, not the data: every reader re-runs `stageFor`, which reads whatever it needs from here. */
export function stageVersion(): number {
  return version;
}

/** The server renders once and never learns anything, so its snapshot has to be a constant. */
export function stageServerVersion(): number {
  return 0;
}

/** The row key for an entity — the same string the directory uses, so the two never disagree. */
export function entityKey(entity: DeckEntity): string {
  const id =
    entity.kind === 'artifacts'
      ? entity.slide.id
      : entity.kind === 'documents'
        ? entity.doc.id
        : entity.kind === 'images'
          ? entity.image.id
          : entity.web.id;
  return itemKey(entity.kind, id);
}

/* ── measured images ───────────────────────────────────────────────────────────────────────────── */

/** A deck has a bounded number of images, but a session browsing several does not. */
const MEASURE_CAP = 240;
const measured = new Map<string, { width: number; height: number }>();
const measuring = new Set<string>();

/**
 * The bitmap's real size, or `undefined` while it is still being measured.
 *
 * Reading this KICKS OFF the measurement on a miss. That is a side effect in a render path, which is
 * normally a mistake — it is safe here because it is idempotent (deduplicated by `measuring`), sets no
 * React state synchronously, and `measureImage` never rejects: a source that fails to load resolves to
 * the product's 640x360 fallback rather than leaving the entry pending forever.
 */
export function measuredImageSize(src: string): { width: number; height: number } | undefined {
  if (!src) return undefined;
  const hit = measured.get(src);
  if (hit) return hit;
  // `new Image()` is browser-only, and the server has nothing to report anyway.
  if (typeof window === 'undefined' || measuring.has(src)) return undefined;
  measuring.add(src);
  void measureImage(src).then((size) => {
    measuring.delete(src);
    if (measured.size > MEASURE_CAP) measured.clear();
    measured.set(src, size);
    bump();
  });
  return undefined;
}

/* ── failed renders ────────────────────────────────────────────────────────────────────────────── */

const failures = new Set<string>();

/** Whether this row's renderer has thrown, or its source failed to load, since the last retry. */
export function stageFailed(key: string): boolean {
  return failures.has(key);
}

/** Recorded by the error boundary, and by the `onError` of anything that loads over the network. */
export function markStageFailed(key: string): void {
  if (failures.has(key)) return;
  failures.add(key);
  bump();
}

/** `Try again` on the pane's failed board. Also what a fresh generation should do to a stale failure. */
export function clearStageFailed(key: string): void {
  if (failures.delete(key)) bump();
}
